import { centsToFormattedDollars } from '@fruitful/fruitils';
import { PaycheckRoutingRuleAccountType } from '@fruitful/income-split';
import { Badge } from 'primereact/badge';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React, { useMemo, useState } from 'react';
import {
    BadgeLabel,
    BodyLargeEmphasized,
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    Checkbox,
    CloseButton,
    InfoRow,
    RowList,
    Separator,
    SmoothieScrollView,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { APP_RIGHT_EDGE } from '../../../../common/utils/layout';
import type {
    IncomeSplitExecution,
    MembershipDetails,
} from '../../../entityTypes';
import { parseHtml } from './IncomeSplitHistoryRules';

interface IncomeSplitExecutionDetailModalProps {
    execution: IncomeSplitExecution | null;
    isOpen: boolean;
    onClose: () => void;
    membershipDetails: MembershipDetails;
}

const IncomeSplitExecutionDetailModal = ({
    execution,
    isOpen,
    onClose,
}: IncomeSplitExecutionDetailModalProps) => {
    // State to control showing unreached rule sets (default to false - hide them)
    const [showUnreachedRuleSets, setShowUnreachedRuleSets] = useState(false);
    // State to control showing debugging columns (Target Account ID, Rule ID)
    const [showDebuggingColumns, setShowDebuggingColumns] = useState(false);

    // Extract execution fields with defaults for when execution is null (hooks must run unconditionally)
    const plan = execution?.plan ?? {
        paycheckTransactions: [],
        minimumBalanceAlreadyMets: [],
    };
    const transactions = execution?.transactions ?? [];

    const planningServiceResultPlan = useMemo(() => {
        const planWithPlanningService = plan as {
            planningServiceResultPlan?: {
                ruleSetPlans?: Array<{
                    ruleSetId: string;
                    ruleSetName: string;
                    priority: number;
                    ruleSetReached: boolean;
                    rules: Array<{
                        id: string;
                        priority: number;
                        accountType?: string;
                        destinationAccountDetails?: {
                            nickname: string;
                            syncteraAccountId: string;
                        };
                        destinationInternalInvestmentAccountDetails?: {
                            nickname: string;
                            syncteraAccountId: string;
                        };
                        destinationDepositoryAccountDetails?: {
                            nickname: string;
                            syncteraAccountId: string;
                        };
                        transferInfo: {
                            type: string;
                            amountToTransfer: number;
                            memo?: string | null;
                        };
                        originalRule?: {
                            id: string;
                            description: string | null;
                        };
                    }>;
                }>;
                totalIncomeAmount?: number;
                totalAllocatedAmount?: number;
                finalRemainingFunds?: number;
            };
        };
        return planWithPlanningService.planningServiceResultPlan;
    }, [plan]);

    // Create a map from ruleId/originalRuleId to transaction status, failReason, and transaction ID
    const ruleIdToTransactionMap = useMemo(() => {
        const map = new Map<
            string,
            {
                status: string;
                failReason: string | null;
                transactionId: string | null;
            }
        >();

        transactions.forEach((txn) => {
            const transactionInfo = {
                status: txn.status,
                failReason: txn.failReason,
                transactionId: txn.id ?? null,
            };

            // Map by ruleId
            if (txn.ruleId) {
                map.set(txn.ruleId, transactionInfo);
            }
            // Map by originalRuleId
            if (txn.originalRuleId) {
                map.set(txn.originalRuleId, transactionInfo);
            }
        });

        return map;
    }, [transactions]);

    const newSplitPlanData = useMemo(() => {
        const tableData: Array<{
            ruleSetId: string;
            ruleSetName: string;
            ruleSetReached: boolean;
            targetAccount: string;
            targetAccountId: string;
            amount: string;
            memo: string;
            type: string;
            ruleId: string;
            transferStatus: string | null;
            failReason: string | null;
            transactionId: string | null;
            transactionStatus: string | null;
        }> = [];

        // Primary: Use planning service result plan if available
        if (planningServiceResultPlan?.ruleSetPlans) {
            const ruleSetPlans = planningServiceResultPlan.ruleSetPlans;

            ruleSetPlans.forEach((ruleSet) => {
                ruleSet.rules?.forEach((rule) => {
                    const ruleId = rule.originalRule?.id ?? rule.id ?? 'N/A';

                    // Get the appropriate destination account details based on account type
                    // Prioritize account-type-specific details to ensure we get the correct nickname
                    const destinationAccount =
                        rule.accountType ===
                        PaycheckRoutingRuleAccountType.INTERNAL_INVESTMENT
                            ? (rule.destinationInternalInvestmentAccountDetails ??
                              rule.destinationAccountDetails)
                            : rule.accountType ===
                                PaycheckRoutingRuleAccountType.INTERNAL_DEPOSITORY
                              ? (rule.destinationDepositoryAccountDetails ??
                                rule.destinationAccountDetails)
                              : rule.destinationAccountDetails;

                    const targetAccountId =
                        destinationAccount?.syncteraAccountId ?? 'N/A';
                    const transferAmount =
                        rule.transferInfo?.amountToTransfer ?? 0;

                    // Match transaction by ruleId only
                    const transactionInfo =
                        ruleId !== 'N/A'
                            ? ruleIdToTransactionMap.get(ruleId)
                            : null;

                    const transferStatus = transactionInfo?.status ?? null;
                    const failReason = transactionInfo?.failReason ?? null;
                    const transactionId =
                        transactionInfo?.transactionId ?? null;
                    const transactionStatus = transferStatus; // Same as transferStatus for consistency
                    const isFailed = transferStatus === 'FAILED';

                    // Build memo with fail reason if transfer failed
                    let memo =
                        rule.transferInfo?.memo ??
                        rule.originalRule?.description ??
                        ruleSet.ruleSetName ??
                        'N/A';
                    if (isFailed && failReason != null && failReason !== '') {
                        memo = `${memo} - ${failReason}`;
                    }

                    tableData.push({
                        ruleSetId: ruleSet.ruleSetId,
                        ruleSetName: ruleSet.ruleSetName,
                        ruleSetReached: ruleSet.ruleSetReached,
                        targetAccount: destinationAccount?.nickname ?? 'N/A',
                        targetAccountId,
                        amount: centsToFormattedDollars(transferAmount),
                        memo,
                        type: rule.transferInfo?.type ?? 'N/A',
                        ruleId,
                        transferStatus,
                        failReason,
                        transactionId,
                        transactionStatus,
                    });
                });
            });

            // Filter: always show reached rule sets, conditionally show unreached ones
            return tableData.filter(
                (row) => row.ruleSetReached || showUnreachedRuleSets,
            );
        }

        // Fallback: Build table directly from transactions array
        if (Array.isArray(transactions) && transactions.length > 0) {
            transactions.forEach((txn) => {
                const ruleId = txn.ruleId ?? txn.originalRuleId ?? 'N/A';
                const isFailed = txn.status === 'FAILED';

                // Build memo with fail reason if transfer failed
                // Note: memo may exist at runtime even if not in TypeScript type
                const txnWithMemo = txn as unknown as { memo?: string };
                let memo = txnWithMemo.memo ?? 'N/A';
                if (
                    isFailed &&
                    txn.failReason != null &&
                    txn.failReason !== ''
                ) {
                    memo = `${memo} - ${txn.failReason}`;
                }

                tableData.push({
                    ruleSetId: 'N/A', // Not available in transactions
                    ruleSetName: 'N/A', // Not available in transactions
                    ruleSetReached: true, // Assume reached if transaction exists
                    targetAccount: txn.destinationAccountDisplayName ?? 'N/A',
                    targetAccountId: txn.destinationAccountId ?? 'N/A',
                    amount: centsToFormattedDollars(txn.amount),
                    memo,
                    type: txn.transferType ?? 'N/A',
                    ruleId,
                    transferStatus: txn.status,
                    failReason: txn.failReason,
                    transactionId: txn.id ?? null,
                    transactionStatus: txn.status,
                });
            });

            return tableData;
        }

        // No data available
        return [];
    }, [
        planningServiceResultPlan,
        showUnreachedRuleSets,
        ruleIdToTransactionMap,
        transactions,
    ]);

    const sourceAccountName = useMemo(() => {
        if (transactions.length > 0 && transactions[0]) {
            return transactions[0].sourceAccountDisplayName;
        }
        return null;
    }, [transactions]);

    const executionStatusSeverity = (status: string) => {
        switch (status) {
            case 'SUCCEEDED':
                return 'success';
            case 'FAILED':
                return 'danger';
            case 'IN_PROGRESS':
                return 'warning';
            default:
                return 'info';
        }
    };

    if (!execution || !isOpen) return null;

    const {
        id,
        webhookPostedAt,
        processStartedAt,
        processEndedAt,
        amount,
        transactionDescription,
        transactionUuid,
        executionStatus,
        totalPlannedAmount,
        totalExecutedAmount,
        successfulTransfers,
        failedTransfers,
        errorMessage,
        emails,
    } = execution;

    return (
        <View
            style={{
                position: 'fixed',
                top: 50,
                left: 0,
                right: APP_RIGHT_EDGE,
                bottom: 0,
                zIndex: 1000,
            }}
            backgroundColor="rgba(0, 0, 0, 0.5)"
            onPress={onClose}
            alignItems="center"
            justifyContent="center"
        >
            <View
                backgroundColor="white"
                borderRadius={8}
                borderWidth={1}
                borderColor="$gray200"
                width="90%"
                maxHeight="90vh"
                onPress={(e) => {
                    e.stopPropagation();
                }}
            >
                <SmoothieScrollView
                    maxHeight="90vh"
                    containerStyle={{
                        width: '100%',
                        maxWidth: '100%',
                        marginHorizontal: 0,
                        $sm: {
                            width: '100%',
                            maxWidth: '100%',
                            alignSelf: 'stretch',
                        },
                    }}
                >
                    <YStack gap={20} padding={32} width="100%">
                        <YStack gap={16}>
                            <XStack
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <BodyLargeEmphasized>
                                    Income Split Execution Details
                                </BodyLargeEmphasized>
                                <CloseButton onPress={onClose} />
                            </XStack>

                            <Separator borderColor="$gray500" />

                            <XStack gap={16} flexWrap="wrap">
                                <YStack
                                    gap={16}
                                    flex={1}
                                    minWidth={300}
                                    borderRadius={12}
                                    padding={16}
                                    borderWidth={1}
                                    borderColor="$gray300"
                                >
                                    <XStack
                                        justifyContent="space-between"
                                        alignItems="center"
                                    >
                                        <BodyMediumEmphasized>
                                            Execution Details
                                        </BodyMediumEmphasized>
                                        <Badge
                                            value={executionStatus}
                                            severity={executionStatusSeverity(
                                                executionStatus,
                                            )}
                                        />
                                    </XStack>
                                    <RowList
                                        rowHeaderProps={{ title: 'Execution' }}
                                    >
                                        <InfoRow
                                            title="Execution ID"
                                            value={id}
                                        />
                                    </RowList>

                                    <RowList
                                        rowHeaderProps={{ title: 'Timing' }}
                                    >
                                        <InfoRow
                                            title="Process Started At"
                                            value={new Date(
                                                processStartedAt,
                                            ).toLocaleString()}
                                        />
                                        <InfoRow
                                            title="Process Ended At"
                                            value={
                                                processEndedAt != null
                                                    ? new Date(
                                                          processEndedAt,
                                                      ).toLocaleString()
                                                    : 'N/A'
                                            }
                                        />
                                    </RowList>

                                    <RowList
                                        rowHeaderProps={{
                                            title: 'Financial Summary',
                                        }}
                                    >
                                        <InfoRow
                                            title="Total Planned"
                                            value={
                                                totalPlannedAmount != null
                                                    ? centsToFormattedDollars(
                                                          totalPlannedAmount,
                                                      )
                                                    : 'N/A'
                                            }
                                        />
                                        <InfoRow
                                            title="Total Executed"
                                            value={
                                                totalExecutedAmount != null
                                                    ? centsToFormattedDollars(
                                                          totalExecutedAmount,
                                                      )
                                                    : 'N/A'
                                            }
                                        />
                                        <InfoRow
                                            title="Remainder Amount"
                                            value={
                                                planningServiceResultPlan?.finalRemainingFunds !=
                                                null
                                                    ? centsToFormattedDollars(
                                                          planningServiceResultPlan.finalRemainingFunds,
                                                      )
                                                    : totalExecutedAmount !=
                                                            null &&
                                                        amount != null
                                                      ? centsToFormattedDollars(
                                                            amount -
                                                                totalExecutedAmount,
                                                        )
                                                      : 'N/A'
                                            }
                                        />
                                    </RowList>

                                    <YStack gap={6}>
                                        <BodySmallRegular
                                            color="$gray1000"
                                            fontWeight="600"
                                        >
                                            Transfer Status
                                        </BodySmallRegular>
                                        <XStack gap={12} flexWrap="wrap">
                                            <BadgeLabel
                                                variant="positive"
                                                text={`${successfulTransfers} successful`}
                                                size="regular"
                                            />
                                            {failedTransfers > 0 && (
                                                <BadgeLabel
                                                    variant="danger"
                                                    text={`${failedTransfers} failed`}
                                                    size="regular"
                                                />
                                            )}
                                        </XStack>
                                    </YStack>
                                    {errorMessage && (
                                        <YStack
                                            gap={4}
                                            padding={8}
                                            borderRadius={8}
                                            backgroundColor="$red200"
                                            marginTop={8}
                                        >
                                            <BodySmallRegular color="$red1000">
                                                Error Message:
                                            </BodySmallRegular>
                                            <BodySmallRegular color="$red1000">
                                                {errorMessage.message}
                                            </BodySmallRegular>
                                            <BodySmallRegular
                                                color="$red1000"
                                                fontSize={12}
                                            >
                                                {errorMessage.name}
                                            </BodySmallRegular>
                                            <BodySmallRegular
                                                color="$red1000"
                                                fontSize={10}
                                                style={{
                                                    whiteSpace: 'pre-wrap',
                                                }}
                                            >
                                                {errorMessage.stack}
                                            </BodySmallRegular>
                                            <BodySmallRegular
                                                color="$red1000"
                                                fontSize={10}
                                            >
                                                Timestamp:{' '}
                                                {new Date(
                                                    errorMessage.timestamp,
                                                ).toLocaleString()}
                                            </BodySmallRegular>
                                        </YStack>
                                    )}
                                </YStack>
                                <YStack
                                    gap={6}
                                    flex={1}
                                    minWidth={300}
                                    borderRadius={12}
                                    padding={16}
                                    borderWidth={1}
                                    borderColor="$gray300"
                                >
                                    <BodyMediumEmphasized>
                                        Transaction Details
                                    </BodyMediumEmphasized>
                                    <RowList
                                        rowHeaderProps={{
                                            title: 'Transaction Info',
                                        }}
                                    >
                                        <InfoRow
                                            title="Transaction UUID"
                                            value={transactionUuid}
                                        />
                                        <InfoRow
                                            title="Amount"
                                            value={centsToFormattedDollars(
                                                amount,
                                            )}
                                        />
                                        {sourceAccountName != null &&
                                            sourceAccountName !== '' && (
                                                <InfoRow
                                                    title="Deposited into"
                                                    value={sourceAccountName}
                                                />
                                            )}
                                        <InfoRow
                                            title="Description"
                                            value={transactionDescription}
                                        />
                                    </RowList>

                                    <RowList
                                        rowHeaderProps={{ title: 'Timing' }}
                                    >
                                        <InfoRow
                                            title="Webhook Posted At"
                                            value={new Date(
                                                webhookPostedAt,
                                            ).toLocaleString()}
                                        />
                                        {emails.length > 0 &&
                                            emails[0]?.transactionDate !=
                                                null &&
                                            emails[0]?.transactionDate !==
                                                '' && (
                                                <InfoRow
                                                    title="Transaction Date"
                                                    value={new Date(
                                                        emails[0]
                                                            .transactionDate,
                                                    ).toLocaleString()}
                                                />
                                            )}
                                    </RowList>
                                </YStack>
                            </XStack>
                        </YStack>

                        <>
                            <Separator borderColor="$gray500" />

                            <YStack gap={12}>
                                <BodyLargeEmphasized>
                                    Transfer Details
                                </BodyLargeEmphasized>
                                {sourceAccountName != null &&
                                    sourceAccountName !== '' && (
                                        <BodyMediumRegular>
                                            Source Account: {sourceAccountName}
                                        </BodyMediumRegular>
                                    )}
                                <XStack gap={12} flexWrap="wrap">
                                    {planningServiceResultPlan?.ruleSetPlans &&
                                        planningServiceResultPlan.ruleSetPlans.some(
                                            (rs) => !rs.ruleSetReached,
                                        ) && (
                                            <XStack gap={6} alignItems="center">
                                                <View
                                                    style={{
                                                        transform: [
                                                            { scale: 0.8 },
                                                        ],
                                                    }}
                                                >
                                                    <Checkbox
                                                        id="show-unreached-rulesets"
                                                        size="default"
                                                        checked={
                                                            showUnreachedRuleSets
                                                        }
                                                        onPress={() => {
                                                            setShowUnreachedRuleSets(
                                                                (prev) => !prev,
                                                            );
                                                        }}
                                                    />
                                                </View>
                                                <BodySmallRegular>
                                                    unreached rulesets
                                                </BodySmallRegular>
                                            </XStack>
                                        )}
                                    <XStack gap={6} alignItems="center">
                                        <View
                                            style={{
                                                transform: [{ scale: 0.8 }],
                                            }}
                                        >
                                            <Checkbox
                                                id="show-debugging-columns"
                                                size="default"
                                                checked={showDebuggingColumns}
                                                onPress={() => {
                                                    setShowDebuggingColumns(
                                                        (prev) => !prev,
                                                    );
                                                }}
                                            />
                                        </View>
                                        <BodySmallRegular>
                                            debugging columns
                                        </BodySmallRegular>
                                    </XStack>
                                </XStack>
                                <YStack
                                    overflow="hidden"
                                    width="100%"
                                    minWidth="100%"
                                >
                                    <DataTable
                                        value={newSplitPlanData}
                                        tableStyle={{
                                            width: '100%',
                                            tableLayout: 'fixed',
                                        }}
                                        size="small"
                                        emptyMessage="No planned transfers"
                                        showHeaders={
                                            newSplitPlanData.length > 0
                                        }
                                        scrollable
                                        scrollHeight="400px"
                                    >
                                        <Column
                                            field="ruleSetName"
                                            header="Rule Set"
                                            style={{
                                                width: '12%',
                                                minWidth: 150,
                                            }}
                                            body={(rowData: {
                                                ruleSetReached: boolean;
                                                ruleSetName: string;
                                            }) => (
                                                <span
                                                    style={{
                                                        color: rowData.ruleSetReached
                                                            ? undefined
                                                            : '#999',
                                                    }}
                                                >
                                                    {rowData.ruleSetName}
                                                </span>
                                            )}
                                        />
                                        <Column
                                            field="transactionStatus"
                                            header="Status"
                                            style={{
                                                width: '8%',
                                                minWidth: 80,
                                            }}
                                            body={(rowData: {
                                                ruleSetReached: boolean;
                                                transactionStatus:
                                                    | string
                                                    | null;
                                                transferStatus: string | null;
                                            }) => {
                                                const status =
                                                    rowData.transactionStatus;
                                                if (
                                                    status == null ||
                                                    status === '' ||
                                                    status === 'N/A'
                                                ) {
                                                    return null;
                                                }

                                                const isSuccess =
                                                    status.toUpperCase() ===
                                                        'SUCCESS' ||
                                                    status.toUpperCase() ===
                                                        'SUCCEEDED';
                                                const isFailed =
                                                    status.toUpperCase() ===
                                                        'FAIL' ||
                                                    status.toUpperCase() ===
                                                        'FAILED';

                                                return (
                                                    <BadgeLabel
                                                        variant={
                                                            isFailed
                                                                ? 'danger'
                                                                : isSuccess
                                                                  ? 'positive'
                                                                  : 'neutral'
                                                        }
                                                        text={status}
                                                        size="regular"
                                                    />
                                                );
                                            }}
                                        />
                                        <Column
                                            field="targetAccount"
                                            header="Target Account"
                                            style={{
                                                width: '12%',
                                                minWidth: 120,
                                            }}
                                            body={(rowData: {
                                                ruleSetReached: boolean;
                                                targetAccount: string;
                                                transferStatus: string | null;
                                            }) => {
                                                const isFailed =
                                                    rowData.transferStatus ===
                                                    'FAILED';
                                                return (
                                                    <span
                                                        style={{
                                                            color: isFailed
                                                                ? '#d32f2f'
                                                                : rowData.ruleSetReached
                                                                  ? undefined
                                                                  : '#999',
                                                        }}
                                                    >
                                                        {rowData.targetAccount}
                                                    </span>
                                                );
                                            }}
                                        />
                                        <Column
                                            field="amount"
                                            header="Amount"
                                            style={{
                                                width: '10%',
                                                minWidth: 100,
                                            }}
                                            body={(rowData: {
                                                ruleSetReached: boolean;
                                                amount: string;
                                                transferStatus: string | null;
                                            }) => {
                                                const isFailed =
                                                    rowData.transferStatus ===
                                                    'FAILED';
                                                return (
                                                    <span
                                                        style={{
                                                            color: isFailed
                                                                ? '#d32f2f'
                                                                : rowData.ruleSetReached
                                                                  ? undefined
                                                                  : '#999',
                                                        }}
                                                    >
                                                        {rowData.amount}
                                                    </span>
                                                );
                                            }}
                                        />
                                        <Column
                                            field="memo"
                                            header="Memo"
                                            style={{
                                                width: '20%',
                                                minWidth: 200,
                                            }}
                                            body={(rowData: {
                                                ruleSetReached: boolean;
                                                memo: string;
                                                transferStatus: string | null;
                                            }) => {
                                                const isFailed =
                                                    rowData.transferStatus ===
                                                    'FAILED';
                                                return (
                                                    <span
                                                        style={{
                                                            color: isFailed
                                                                ? '#d32f2f'
                                                                : rowData.ruleSetReached
                                                                  ? undefined
                                                                  : '#999',
                                                        }}
                                                    >
                                                        {rowData.memo}
                                                    </span>
                                                );
                                            }}
                                        />
                                        <Column
                                            field="type"
                                            header="Type"
                                            style={{
                                                width: '10%',
                                                minWidth: 120,
                                            }}
                                            body={(rowData: {
                                                ruleSetReached: boolean;
                                                type: string;
                                                transferStatus: string | null;
                                            }) => {
                                                const isFailed =
                                                    rowData.transferStatus ===
                                                    'FAILED';
                                                return (
                                                    <BadgeLabel
                                                        variant={
                                                            isFailed
                                                                ? 'danger'
                                                                : rowData.ruleSetReached
                                                                  ? 'neutral'
                                                                  : 'warning'
                                                        }
                                                        text={rowData.type}
                                                        size="regular"
                                                    />
                                                );
                                            }}
                                        />
                                        {showDebuggingColumns && (
                                            <Column
                                                field="targetAccountId"
                                                header="Target Account ID"
                                                style={{
                                                    width: '18%',
                                                    minWidth: 200,
                                                }}
                                                body={(rowData: {
                                                    ruleSetReached: boolean;
                                                    targetAccountId: string;
                                                    transferStatus:
                                                        | string
                                                        | null;
                                                }) => {
                                                    const isFailed =
                                                        rowData.transferStatus ===
                                                        'FAILED';
                                                    return (
                                                        <span
                                                            style={{
                                                                color: isFailed
                                                                    ? '#d32f2f'
                                                                    : rowData.ruleSetReached
                                                                      ? undefined
                                                                      : '#999',
                                                                fontSize:
                                                                    '12px',
                                                            }}
                                                        >
                                                            {
                                                                rowData.targetAccountId
                                                            }
                                                        </span>
                                                    );
                                                }}
                                            />
                                        )}
                                        {showDebuggingColumns && (
                                            <Column
                                                field="ruleId"
                                                header="Rule ID"
                                                style={{
                                                    width: '18%',
                                                    minWidth: 200,
                                                }}
                                                body={(rowData: {
                                                    ruleSetReached: boolean;
                                                    ruleId: string;
                                                    transferStatus:
                                                        | string
                                                        | null;
                                                }) => {
                                                    const isFailed =
                                                        rowData.transferStatus ===
                                                        'FAILED';
                                                    return (
                                                        <span
                                                            style={{
                                                                color: isFailed
                                                                    ? '#d32f2f'
                                                                    : rowData.ruleSetReached
                                                                      ? undefined
                                                                      : '#999',
                                                                fontSize:
                                                                    '12px',
                                                            }}
                                                        >
                                                            {rowData.ruleId}
                                                        </span>
                                                    );
                                                }}
                                            />
                                        )}
                                        {showDebuggingColumns && (
                                            <Column
                                                field="transactionId"
                                                header="Transaction ID"
                                                style={{
                                                    width: '18%',
                                                    minWidth: 200,
                                                }}
                                                body={(rowData: {
                                                    ruleSetReached: boolean;
                                                    transactionId:
                                                        | string
                                                        | null;
                                                    transferStatus:
                                                        | string
                                                        | null;
                                                }) => {
                                                    const isFailed =
                                                        rowData.transferStatus ===
                                                        'FAILED';
                                                    return (
                                                        <span
                                                            style={{
                                                                color: isFailed
                                                                    ? '#d32f2f'
                                                                    : rowData.ruleSetReached
                                                                      ? undefined
                                                                      : '#999',
                                                                fontSize:
                                                                    '12px',
                                                            }}
                                                        >
                                                            {rowData.transactionId ??
                                                                'N/A'}
                                                        </span>
                                                    );
                                                }}
                                            />
                                        )}
                                        {showDebuggingColumns && (
                                            <Column
                                                field="failReason"
                                                header="Fail Reason"
                                                style={{
                                                    width: '20%',
                                                    minWidth: 200,
                                                }}
                                                body={(rowData: {
                                                    ruleSetReached: boolean;
                                                    failReason: string | null;
                                                    transferStatus:
                                                        | string
                                                        | null;
                                                }) => {
                                                    const isFailed =
                                                        rowData.transferStatus ===
                                                        'FAILED';
                                                    return (
                                                        <span
                                                            style={{
                                                                color: isFailed
                                                                    ? '#d32f2f'
                                                                    : rowData.ruleSetReached
                                                                      ? undefined
                                                                      : '#999',
                                                                fontSize:
                                                                    '12px',
                                                            }}
                                                        >
                                                            {rowData.failReason ??
                                                                'N/A'}
                                                        </span>
                                                    );
                                                }}
                                            />
                                        )}
                                    </DataTable>
                                </YStack>
                                {planningServiceResultPlan?.finalRemainingFunds !=
                                    null && (
                                    <BodyMediumRegular>
                                        {planningServiceResultPlan.finalRemainingFunds >
                                        0 ? (
                                            <>
                                                {centsToFormattedDollars(
                                                    planningServiceResultPlan.finalRemainingFunds,
                                                )}{' '}
                                                Kept in{' '}
                                                {sourceAccountName ??
                                                    'source account'}{' '}
                                                as remainder.
                                            </>
                                        ) : (
                                            <>
                                                Remainder is $0.00. All funds
                                                allocated.
                                            </>
                                        )}
                                    </BodyMediumRegular>
                                )}
                            </YStack>
                        </>

                        {plan.validationResult?.validationErrors != null &&
                            plan.validationResult.validationErrors.length >
                                0 && (
                                <>
                                    <Separator borderColor="$gray500" />
                                    <YStack gap={16}>
                                        <BodyLargeEmphasized>
                                            Validation Errors
                                        </BodyLargeEmphasized>
                                        <YStack
                                            gap={8}
                                            padding={12}
                                            borderRadius={8}
                                            backgroundColor={
                                                plan.validationResult.breaking
                                                    ? '$red200'
                                                    : '$orange200'
                                            }
                                            borderWidth={1}
                                            borderColor={
                                                plan.validationResult.breaking
                                                    ? '$red600'
                                                    : '$orange600'
                                            }
                                        >
                                            {plan.validationResult.validationErrors.map(
                                                (error, index) => (
                                                    <BodySmallRegular
                                                        key={index}
                                                        color={
                                                            plan
                                                                .validationResult
                                                                ?.breaking
                                                                ? '$red1000'
                                                                : '$orange1000'
                                                        }
                                                    >
                                                        • {error}
                                                    </BodySmallRegular>
                                                ),
                                            )}
                                        </YStack>
                                    </YStack>
                                </>
                            )}

                        {emails.length > 0 && (
                            <>
                                <Separator borderColor="$gray500" />
                                <YStack gap={16}>
                                    <BodyLargeEmphasized>
                                        Email Details
                                    </BodyLargeEmphasized>
                                    {emails.map((email) => (
                                        <YStack
                                            key={email.id}
                                            gap={12}
                                            borderRadius={12}
                                            backgroundColor="$gray200"
                                            padding={16}
                                            borderWidth={1}
                                            borderColor="$gray300"
                                        >
                                            <XStack
                                                justifyContent="space-between"
                                                alignItems="center"
                                                paddingBottom={8}
                                                borderBottomWidth={1}
                                                borderBottomColor="$gray300"
                                            >
                                                <BodyMediumEmphasized>
                                                    Sent on:{' '}
                                                    {new Date(
                                                        email.createdAt,
                                                    ).toLocaleString()}
                                                </BodyMediumEmphasized>
                                            </XStack>
                                            <BodyMediumRegular color="$gray1100">
                                                Email Copy
                                            </BodyMediumRegular>
                                            <YStack
                                                gap={8}
                                                padding={12}
                                                borderRadius={8}
                                                backgroundColor="$white"
                                                borderWidth={1}
                                                borderColor="$gray300"
                                            >
                                                <YStack gap={8}>
                                                    {parseHtml(
                                                        email.emailCopy,
                                                    ).map((item, itemIndex) => (
                                                        <YStack
                                                            key={itemIndex}
                                                            gap={6}
                                                        >
                                                            <BodySmallRegular
                                                                color="$gray1100"
                                                                fontWeight="600"
                                                            >
                                                                {item.title}
                                                            </BodySmallRegular>
                                                            {item.children
                                                                .length > 0 && (
                                                                <YStack
                                                                    gap={4}
                                                                    paddingLeft={
                                                                        12
                                                                    }
                                                                >
                                                                    {item.children.map(
                                                                        (
                                                                            child,
                                                                            childIndex,
                                                                        ) => (
                                                                            <XStack
                                                                                key={
                                                                                    childIndex
                                                                                }
                                                                                gap={
                                                                                    6
                                                                                }
                                                                                alignItems="flex-start"
                                                                            >
                                                                                <BodySmallRegular
                                                                                    color="$gray1100"
                                                                                    paddingTop={
                                                                                        2
                                                                                    }
                                                                                >
                                                                                    •
                                                                                </BodySmallRegular>
                                                                                <BodySmallRegular
                                                                                    color="$gray1100"
                                                                                    flex={
                                                                                        1
                                                                                    }
                                                                                >
                                                                                    {
                                                                                        child
                                                                                    }
                                                                                </BodySmallRegular>
                                                                            </XStack>
                                                                        ),
                                                                    )}
                                                                </YStack>
                                                            )}
                                                        </YStack>
                                                    ))}
                                                </YStack>
                                            </YStack>
                                        </YStack>
                                    ))}
                                </YStack>
                            </>
                        )}
                    </YStack>
                </SmoothieScrollView>
            </View>
        </View>
    );
};

export default IncomeSplitExecutionDetailModal;
